import React, { useEffect, useMemo, useState } from 'react';
import Popup from 'reactjs-popup';
import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';
import { useHttpGetRetailerGateways } from '../../hooks';
import { Input, Select } from '../../components/Common';
import Loader from '../../components/Loader';
import Pagination from '../../components/Pagination';
import CopyButton from '../../components/CopyButton';
import GatewayKpis from './GatewayKpis';
import { HEALTH_BUCKET_LIST, lastCompleteHourWindow } from './gatewayHealth';
import PriceAreaSummary from './PriceAreaSummary';
import HeldGatewayOnboardings from './HeldGatewayOnboardings';
import DeleteGatewayModal from './DeleteGatewayModal';

const PAGE_SIZE = 50;

const STATUS_META = {
    CONNECTED: { label: 'Connected', className: 'gateways-overview__status--connected' },
    LOST: { label: 'Lost', className: 'gateways-overview__status--lost' },
};

const STATUS_FILTER_OPTIONS = [
    { value: '', label: 'All statuses' },
    { value: 'CONNECTED', label: 'Connected' },
    { value: 'LOST', label: 'Lost' },
];

// Container style for the per-row kebab actions menu (reactjs-popup content prop);
// the trigger and item styling live in _gateways.scss so they get hover states.
const MENU_STYLE = {
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    padding: 4,
    width: 140,
};

const formatDateTime = value => {
    if (!value) {
        return '—';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

const statusMetaFor = status => STATUS_META[status] || { label: 'Unknown', className: 'gateways-overview__status--unknown' };

// Per-field text filters (each matched server-side, AND-combined), like the AllDevices table.
const TEXT_FILTERS = [
    { key: 'name', placeholder: 'Name' },
    { key: 'reference', placeholder: 'Serial' },
    { key: 'device_id', placeholder: 'Device ID' },
    { key: 'customer', placeholder: 'Customer' },
    { key: 'address', placeholder: 'Address' },
];

const EMPTY_FILTERS = { name: '', reference: '', device_id: '', customer: '', address: '', status: '' };

// The columns the API can sort by, keyed by the value its `sort` param takes. Sorting
// happens server-side, over the whole filtered set before it is paginated, so these are
// exactly the four the route supports — the rest of the columns stay plain headers.
// `defaultOrder` is the direction a column starts in when it is first picked: A-Z for
// text, newest first for dates, which is the question an operator clicks a date for.
const SORT_COLUMNS = {
    name: { label: 'Name', defaultOrder: 'asc' },
    status: { label: 'Status', defaultOrder: 'asc' },
    created: { label: 'Onboarded at', defaultOrder: 'desc' },
    last_seen: { label: 'Last seen', defaultOrder: 'desc' },
};

const ARIA_SORT = { asc: 'ascending', desc: 'descending' };
const SORT_ICON = { asc: 'fa-sort-up', desc: 'fa-sort-down' };

const GatewaysOverview = () => {
    const history = useHistory();

    // `rawFilters` drives the inputs immediately; `filters` is debounced and sent to the API.
    const [rawFilters, setRawFilters] = useState(EMPTY_FILTERS);
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [currentPage, setCurrentPage] = useState(1);

    // Sent to the API rather than applied here: the page only holds PAGE_SIZE rows, so
    // sorting in the browser would reorder the page instead of the fleet.
    const [sort, setSort] = useState({ column: 'last_seen', order: 'desc' });

    // Fleet-health bucket filter, built by clicking cards in the KPI section (empty = off).
    // Several buckets can be selected at once; the API unions them, so the list shows every
    // gateway in any selected bucket. The classification window (the last complete hour) is
    // computed once on mount and shared with the cards, so the filtered table counts exactly
    // what the cards counted.
    const [{ from: healthFrom, to: healthTo }] = useState(() => lastCompleteHourWindow());
    const [healthBuckets, setHealthBuckets] = useState([]);

    useEffect(() => {
        const handle = setTimeout(() => {
            setFilters(rawFilters);
            setCurrentPage(1);
        }, 300);
        return () => clearTimeout(handle);
    }, [rawFilters]);

    const offset = (currentPage - 1) * PAGE_SIZE;

    const params = useMemo(() => {
        const p = { limit: PAGE_SIZE, offset, sort: sort.column, order: sort.order };
        Object.entries(filters).forEach(([key, value]) => {
            const v = (value ?? '').trim();
            if (v) {
                p[key] = v;
            }
        });
        if (healthBuckets.length) {
            p.health = healthBuckets.join(',');
            p.health_from = healthFrom;
            p.health_to = healthTo;
        }
        return p;
    }, [offset, filters, sort, healthBuckets, healthFrom, healthTo]);

    // The row whose Delete action is open (null = no modal). Deleting from the list keeps
    // the operator on the list, so a successful delete just refetches the current page.
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [reloadToken, setReloadToken] = useState(0);

    const { gateways, total, isReady, error } = useHttpGetRetailerGateways(params, reloadToken);

    const setFilter = (key, value) => setRawFilters(prev => ({ ...prev, [key]: value }));
    const toggleHealthBucket = bucket => {
        setHealthBuckets(prev => (prev.includes(bucket) ? prev.filter(b => b !== bucket) : [...prev, bucket]));
        setCurrentPage(1);
    };
    // Picking the sorted column flips it; picking another starts at that column's own
    // direction. Either way the list goes back to page 1 — page 4 of a differently
    // ordered list is not the page the operator was looking at.
    const toggleSort = column => {
        setSort(prev =>
            prev.column === column ? { column, order: prev.order === 'asc' ? 'desc' : 'asc' } : { column, order: SORT_COLUMNS[column].defaultOrder }
        );
        setCurrentPage(1);
    };
    const clearFilters = () => {
        setRawFilters(EMPTY_FILTERS);
        setHealthBuckets([]);
        setCurrentPage(1);
    };
    const hasFilters = Object.values(rawFilters).some(v => v) || healthBuckets.length > 0;
    // Listed in card order rather than click order, so the subtitle reads the same way
    // however the operator built the selection.
    const healthBucketLabel = HEALTH_BUCKET_LIST.filter(bucket => healthBuckets.includes(bucket.overviewKey))
        .map(bucket => bucket.label)
        .join(', ');

    const goToGateway = gateway => {
        if (gateway.device_id) {
            // Pass the row through router state so the page shows name/serial/ids instantly.
            history.push({ pathname: `/gateways/${gateway.device_id}`, state: { gateway } });
        } else if (gateway.retailer_customer_id) {
            history.push(`/devicesdetail/${gateway.retailer_customer_id}/gateways`);
        }
    };

    const sortableHeader = key => {
        const { label } = SORT_COLUMNS[key];
        const active = sort.column === key;
        return (
            <th aria-sort={active ? ARIA_SORT[sort.order] : 'none'}>
                <button type="button" className="gateways-overview__sort" onClick={() => toggleSort(key)}>
                    <i className={`fa-solid ${active ? SORT_ICON[sort.order] : 'fa-sort'}`} />
                    {label}
                </button>
            </th>
        );
    };

    const renderBody = () => {
        if (!isReady) {
            return <Loader />;
        }
        if (error) {
            return <div className="gateways-overview__message gateways-overview__message--error">Could not load gateways.</div>;
        }
        if (gateways.length === 0) {
            return <div className="gateways-overview__message">No gateways found.</div>;
        }
        return (
            <>
                <div className="gateways-overview__table-wrapper">
                    <table className="gateways-overview__table">
                        <thead>
                            <tr>
                                {sortableHeader('name')}
                                <th>Customer</th>
                                <th>Address</th>
                                <th>Serial</th>
                                <th>Device ID</th>
                                {sortableHeader('status')}
                                <th>Firmware</th>
                                {sortableHeader('created')}
                                {sortableHeader('last_seen')}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {gateways.map(gateway => {
                                const meta = statusMetaFor((gateway.health?.status || '').toUpperCase());
                                return (
                                    <tr key={gateway.gateway_id} className="gateways-overview__row" onClick={() => goToGateway(gateway)}>
                                        <td>{gateway.name || gateway.gateway_id}</td>
                                        <td>{gateway.retailer_customer_id || 'Not onboarded'}</td>
                                        <td>{gateway.address || '—'}</td>
                                        <td>{gateway.reference || '—'}</td>
                                        <td className="gateways-overview__mono">
                                            {gateway.device_id || '—'}
                                            {gateway.device_id ? <CopyButton value={gateway.device_id} /> : null}
                                        </td>
                                        <td>
                                            <span className={`gateways-overview__status ${meta.className}`}>
                                                <span className="gateways-overview__status-dot" />
                                                {meta.label}
                                            </span>
                                        </td>
                                        <td>{gateway.health?.version || '—'}</td>
                                        <td>{formatDateTime(gateway.created)}</td>
                                        <td>{formatDateTime(gateway.health?.last_heartbeat)}</td>
                                        {/* stopPropagation on the cell: neither the kebab nor the menu
                                            items should also fire the row's click-to-open-details. */}
                                        <td onClick={event => event.stopPropagation()}>
                                            <Popup
                                                trigger={
                                                    <button
                                                        type="button"
                                                        aria-label="Gateway actions"
                                                        title="Actions"
                                                        className="gateways-overview__kebab"
                                                    >
                                                        &#8942;
                                                    </button>
                                                }
                                                position={['bottom right', 'top right', 'left center']}
                                                keepTooltipInside
                                                closeOnDocumentClick
                                                on="click"
                                                arrow={false}
                                                contentStyle={MENU_STYLE}
                                            >
                                                {close => (
                                                    <div className="gateways-overview__menu">
                                                        <button
                                                            type="button"
                                                            className="gateways-overview__menu-item"
                                                            onClick={() => {
                                                                close();
                                                                goToGateway(gateway);
                                                            }}
                                                        >
                                                            View
                                                        </button>
                                                        {gateway.device_id ? (
                                                            <button
                                                                type="button"
                                                                className="gateways-overview__menu-item gateways-overview__menu-item--danger"
                                                                onClick={() => {
                                                                    close();
                                                                    setDeleteTarget(gateway);
                                                                }}
                                                            >
                                                                Delete
                                                            </button>
                                                        ) : null}
                                                    </div>
                                                )}
                                            </Popup>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <div className="gateways-overview__pagination">
                    <Pagination elementsPerPage={PAGE_SIZE} totalElements={total} currentPage={currentPage} setCurrentPage={setCurrentPage} />
                </div>
            </>
        );
    };

    return (
        <div className="gateways-overview">
            <h1 className="gateways-overview__title">Gateways</h1>

            <GatewayKpis from={healthFrom} to={healthTo} selectedBuckets={healthBuckets} onToggleBucket={toggleHealthBucket} />

            <PriceAreaSummary />

            <HeldGatewayOnboardings reloadToken={reloadToken} />

            <div className="gateways-overview__list">
                <div className="gateways-overview__list-head">
                    <h2 className="gateways-overview__list-title">
                        All gateways
                        {healthBucketLabel ? <span className="gateways-overview__list-subtitle"> · {healthBucketLabel}</span> : null}
                    </h2>
                    {hasFilters ? (
                        <button type="button" className="gateways-overview__clear" onClick={clearFilters}>
                            Clear filters
                        </button>
                    ) : null}
                </div>

                <div className="gateways-overview__filters">
                    {TEXT_FILTERS.map(({ key, placeholder }) => (
                        <Input
                            key={key}
                            type="text"
                            placeholder={placeholder}
                            value={rawFilters[key]}
                            onChange={e => setFilter(key, e.target.value)}
                        />
                    ))}
                    <Select value={rawFilters.status} options={STATUS_FILTER_OPTIONS} onChange={e => setFilter('status', e.target.value)} />
                </div>

                {renderBody()}
            </div>

            {deleteTarget ? (
                <DeleteGatewayModal
                    deviceId={deleteTarget.device_id}
                    gatewayName={deleteTarget.name}
                    serial={deleteTarget.reference}
                    open
                    setOpen={isOpen => {
                        if (!isOpen) {
                            setDeleteTarget(null);
                        }
                    }}
                    onDeleted={() => setReloadToken(token => token + 1)}
                />
            ) : null}
        </div>
    );
};

export default GatewaysOverview;
